// Shared button components to unify colors and interactions across the app
import styled from 'styled-components';

export const BaseButton = styled.button`
  padding: ${({ $size }) => ($size === 'sm' ? '0.45rem 0.75rem' : '0.75rem 1.25rem')};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme, $size }) => ($size === 'sm' ? '0.8rem' : theme.typography.fontSize.sm)};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  cursor: pointer;
  transition-property: background-color, color, border-color, box-shadow, opacity;
  transition-duration: 0.2s;
  transition-timing-function: ease-in-out;
  display: inline-flex;
  align-items: center;
  gap: ${({ $size }) => ($size === 'sm' ? '0.4rem' : '0.5rem')};

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export const PrimaryButton = styled(BaseButton)`
  background: ${({ theme, $variant }) => {
    switch ($variant) {
  case 'accent': return theme.colors.accent;
      case 'success': return theme.colors.success;
      case 'warning': return theme.colors.warning;
      case 'danger': return theme.colors.danger;
      case 'secondary': return theme.colors.secondary;
      default: return theme.colors.info;
    }
  }};
  color: ${({ theme }) => theme.colors.onPrimary};

  &:hover:not(:disabled) {
    background: ${({ theme, $variant }) => {
      switch ($variant) {
  case 'accent': return theme.colors.accentHover;
        case 'success': return theme.colors.successHover;
        case 'warning': return theme.colors.warningHover;
        case 'danger': return theme.colors.dangerHover;
        case 'secondary': return theme.colors.secondaryHover;
        default: return theme.colors.infoHover;
      }
    }};
  }
`;

export const SecondaryButton = styled(BaseButton)`
  background: ${({ theme }) => theme.colors.secondary};
  color: ${({ theme }) => theme.colors.onPrimary};

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.secondaryHover};
  }
`;

export const IconButton = styled(BaseButton)`
  padding: 0.25rem 0.5rem;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.txtSecondary};

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.surfaceHover};
  }
`;

// Neutral/outline button: integrated with dark theme, subtle background and visible border
export const NeutralButton = styled(BaseButton)`
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.txtPrincipal};
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  border-radius: ${({ theme }) => theme.borderRadius.md};

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.surfaceHover};
  }
`;

export const ActionButton = styled(IconButton)`
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  color: ${({ theme, $variant }) => {
    switch ($variant) {
      case 'primary': return theme.colors.info;
      case 'info': return theme.colors.info;
      case 'success': return theme.colors.success;
      case 'warning': return theme.colors.warning;
      case 'danger': return theme.colors.danger;
      case 'edit': return theme.colors.warning;
      case 'delete': return theme.colors.danger;
      default: return theme.colors.txtSecondary;
    }
  }};
  border-color: ${({ theme, $variant }) => {
    switch ($variant) {
      case 'primary': return theme.colors.info;
      case 'info': return theme.colors.info;
      case 'success': return theme.colors.success;
      case 'warning': return theme.colors.warning;
      case 'danger': return theme.colors.danger;
      case 'edit': return theme.colors.warning;
      case 'delete': return theme.colors.danger;
      default: return theme.colors.borderDefault;
    }
  }};

  &:hover:not(:disabled) {
  background: ${({ theme, $variant }) => {
      switch ($variant) {
    case 'primary': return `${theme.colors.info}1A`;
    case 'info': return `${theme.colors.info}1A`;
    case 'success': return `${theme.colors.success}1A`;
    case 'warning': return `${theme.colors.warning}1A`;
    case 'danger': return `${theme.colors.danger}1A`;
    case 'edit': return `${theme.colors.warning}1A`;
    case 'delete': return `${theme.colors.danger}1A`;
    default: return theme.colors.surfaceHover;
      }
    }};
  color: inherit;
  }
`;

// Outlined buttons (text + border colored, transparent background)
export const OutlinedButton = styled(BaseButton)`
  background: ${({ theme }) => theme.colors.transparent};
  color: ${({ theme, $variant }) => ($variant === 'secondary' ? theme.colors.secondary : theme.colors.info)};
  border: 1px solid ${({ theme, $variant }) => ($variant === 'secondary' ? theme.colors.secondary : theme.colors.info)};

  &:hover:not(:disabled) {
    background: ${({ theme, $variant }) => ($variant === 'secondary' ? theme.colors.neutroAlpha : theme.colors.infoAlpha)};
  }
`;

// Botón chico de navegación/acción secundaria en pies de formulario
// (Anterior/Siguiente/Guardar y nuevo, etc.)
export const NavButton = styled.button`
  background: ${({ theme }) => theme.colors.transparent};
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  color: ${({ theme }) => theme.colors.txtSecundario};
  padding: 0.3rem 0.55rem;
  border-radius: 5px;
  font-size: 0.8rem;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  transition: all 0.2s ease;
  &:hover:not(:disabled) {
    border-color: ${({ theme }) => theme.colors.info};
    color: ${({ theme }) => theme.colors.info};
    background: ${({ theme }) => `${theme.colors.info}15`};
  }
  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }
`;

// Variante de NavButton para "Duplicar"/"Guardar y duplicar": mismo estilo,
// hover en verde para diferenciarlo de las acciones de navegación.
export const DuplicarButton = styled(NavButton)`
  &:hover:not(:disabled) {
    border-color: ${({ theme }) => theme.colors.success};
    color: ${({ theme }) => theme.colors.success};
    background: ${({ theme }) => `${theme.colors.success}15`};
  }
`;

export default BaseButton;
